"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { createAudio, type CreateAudioReturn } from "@/app/_blocks/BlockInteractCreateOmnibox/createAudio";
import { isXiApiUrl } from "@/app/_api/elevenlabs/fetch";
import { getCaptchaToken } from "@/app/_providers/hcaptcha/core";
import { logger } from "@utilities/logger";

type PlayState = "idle" | "loading" | "playing";

type AudioPlayerItem = { id: string; audioUrl: string; loop?: boolean; knownDuration?: number };
type StartPlaybackArgs = AudioPlayerItem & {
  startTime?: number;
  failureMessage: string;
};

interface AudioPlayerContextValue {
  activeId: string | null;
  playState: PlayState;
  analyser: AnalyserNode | null;
  currentTime: number;
  duration: number;
  handlePlay: (item: AudioPlayerItem) => void;
  playFrom: (item: AudioPlayerItem, startTime: number) => void;
  togglePlayPause: () => void;
  stopActive: () => void;
  seek: (time: number) => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextValue | null>(null);

const NOOP_CONTEXT: AudioPlayerContextValue = {
  activeId: null,
  playState: "idle",
  analyser: null,
  currentTime: 0,
  duration: 0,
  handlePlay: () => {},
  playFrom: () => {},
  togglePlayPause: () => {},
  stopActive: () => {},
  seek: () => {},
};

export function useAudioPlayer(): AudioPlayerContextValue {
  return useContext(AudioPlayerContext) ?? NOOP_CONTEXT;
}

export function AudioPlayerProvider({ children }: { children: React.ReactNode }) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [playState, setPlayState] = useState<PlayState>("idle");
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Incremented each time a new audio instance is placed in the map, so that
  // the timeupdate/durationchange subscription effect re-runs even when activeId
  // doesn't change (e.g. Xi API URLs that delay audio creation via captcha fetch).
  const [audioVersion, setAudioVersion] = useState(0);

  const audioMapRef = useRef<Map<string, CreateAudioReturn>>(new Map());
  const analyserMapRef = useRef<Map<string, AnalyserNode | null>>(new Map());
  const knownDurationMapRef = useRef<Map<string, number>>(new Map());
  const activeIdRef = useRef<string | null>(null);
  const playStateRef = useRef<PlayState>("idle");
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Subscribe currentTime/duration to the active audio element
  useEffect(() => {
    if (!activeId) {
      setCurrentTime(0);
      setDuration(0);
      return;
    }
    const audio = audioMapRef.current.get(activeId);
    if (!audio) return;
    const el = audio.getAudio();
    if (!el) return;

    const knownDuration = knownDurationMapRef.current.get(activeId) ?? 0;
    const onTimeUpdate = () => setCurrentTime(el.currentTime);
    const onDurationChange = () => setDuration(isFinite(el.duration) ? el.duration : knownDuration);

    el.addEventListener("timeupdate", onTimeUpdate);
    el.addEventListener("durationchange", onDurationChange);
    // Capture current values immediately
    setCurrentTime(el.currentTime);
    setDuration(isFinite(el.duration) ? el.duration : knownDuration);

    return () => {
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.removeEventListener("durationchange", onDurationChange);
    };
  }, [activeId, audioVersion]);

  const stopActive = useCallback(() => {
    const audioMap = audioMapRef.current;
    const id = activeIdRef.current;
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (id) {
      audioMap.get(id)?.pause();
    }
    playStateRef.current = "idle";
    setPlayState("idle");
    setActiveId(null);
    setAnalyser(null);
    // currentTime/duration reset is handled by the activeId useEffect
    activeIdRef.current = null;
  }, []);

  const seek = useCallback((time: number) => {
    const id = activeIdRef.current;
    if (!id) return;
    const el = audioMapRef.current.get(id)?.getAudio();
    if (el) {
      const maxTime = isFinite(el.duration) ? el.duration : (knownDurationMapRef.current.get(id) ?? 0);
      const clampedTime = Math.max(0, Math.min(time, maxTime));
      el.currentTime = clampedTime;
      setCurrentTime(clampedTime);
    }
  }, []);

  const togglePlayPause = useCallback(() => {
    const audioMap = audioMapRef.current;
    const id = activeIdRef.current;
    if (!id) return;
    const audio = audioMap.get(id);
    if (!audio) return;

    if (playStateRef.current === "playing") {
      // Pause but keep activeId so the player bar stays visible and resumable
      audio.pause();
      playStateRef.current = "idle";
      setPlayState("idle");
    } else {
      // Cancel linger timer so it doesn't close the player while audio resumes.
      // If the timer was active the track had ended naturally, so restart from 0.
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
        const el = audioMap.get(id)?.getAudio();
        if (el) el.currentTime = 0;
      }
      // Resume from paused state
      playStateRef.current = "loading";
      setPlayState("loading");
      audio
        .play()
        .then(() => {
          if (activeIdRef.current === id) {
            playStateRef.current = "playing";
            setPlayState("playing");
            setAnalyser(analyserMapRef.current.get(id) ?? null);
          }
        })
        .catch((err: unknown) => {
          logger.warn("[AudioPlayer] Playback resume failed", { id, err });
          if (activeIdRef.current === id) {
            playStateRef.current = "idle";
            setPlayState("idle");
            setActiveId(null);
            setAnalyser(null);
            activeIdRef.current = null;
          }
        });
    }
  }, []);

  const startPlayback = useCallback(async ({ id, audioUrl, loop, startTime, failureMessage }: StartPlaybackArgs) => {
    const audioMap = audioMapRef.current;
    const analyserMap = analyserMapRef.current;

    try {
      let resolvedUrl = audioUrl;
      if (isXiApiUrl(audioUrl)) {
        const token = await getCaptchaToken({ behaviourType: "sound_effects" });
        const u = new URL(audioUrl);
        u.searchParams.set("hcaptcha_token", token);
        resolvedUrl = u.toString();
      }

      if (activeIdRef.current !== id) return;

      const existingAudio = audioMap.get(id);
      if (existingAudio && isXiApiUrl(audioUrl)) {
        existingAudio.cleanup();
        audioMap.delete(id);
        analyserMap.delete(id);
      }

      let audio = audioMap.get(id);
      if (!audio) {
        audio = createAudio({
          src: resolvedUrl,
          loop,
          fade: false,
          onLoaded: ({ analyser: a }) => {
            analyserMap.set(id, a ?? null);
          },
          onEnded: () => {
            if (activeIdRef.current === id) {
              playStateRef.current = "idle";
              setPlayState("idle");
              setAnalyser(null);
              // Keep activeId set for 3 s so the player bar stays visible after
              // a track ends. Cancelled if another track starts playing first.
              closeTimerRef.current = setTimeout(() => {
                closeTimerRef.current = null;
                if (activeIdRef.current === id) {
                  setActiveId(null);
                  activeIdRef.current = null;
                }
              }, 3000);
            }
          },
        });
        audioMap.set(id, audio);
        audio.load(startTime != null ? { time: startTime } : undefined);
        setAudioVersion(v => v + 1);
      } else if (startTime != null) {
        const el = audio.getAudio();
        if (el) el.currentTime = startTime;
      }

      await audio.play(startTime != null ? { time: startTime } : undefined);
      if (activeIdRef.current === id) {
        playStateRef.current = "playing";
        setPlayState("playing");
        if (startTime != null) setCurrentTime(startTime);
        setAnalyser(analyserMap.get(id) ?? null);
      }
    } catch (err: unknown) {
      logger.warn(failureMessage, { id, err });
      if (activeIdRef.current === id) {
        playStateRef.current = "idle";
        setPlayState("idle");
        setActiveId(null);
        setAnalyser(null);
        activeIdRef.current = null;
      }
    }
  }, []);

  const handlePlay = useCallback(
    ({ id, audioUrl, loop, knownDuration }: AudioPlayerItem) => {
      const audioMap = audioMapRef.current;
      const currentActiveId = activeIdRef.current;

      if (knownDuration != null) {
        knownDurationMapRef.current.set(id, knownDuration);
      }

      // Toggle: clicking the active item stops it entirely, unless it's in the linger window
      // after natural playback end (close timer pending) — in that case restart it.
      if (currentActiveId === id) {
        if (closeTimerRef.current !== null) {
          // Track ended naturally and is still in the 3-second linger; restart instead of closing.
          clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
          const audio = audioMap.get(id);
          if (audio) {
            const el = audio.getAudio();
            if (el) el.currentTime = 0;
            audio
              .play()
              .then(() => {
                if (activeIdRef.current === id) {
                  playStateRef.current = "playing";
                  setPlayState("playing");
                  setAnalyser(analyserMapRef.current.get(id) ?? null);
                }
              })
              .catch(() => {});
          }
          return;
        }
        audioMap.get(id)?.pause();
        playStateRef.current = "idle";
        setPlayState("idle");
        setActiveId(null);
        setAnalyser(null);
        activeIdRef.current = null;
        return;
      }

      // Cancel any pending deferred close from the previous track
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      // Pause whatever was playing before
      if (currentActiveId) {
        audioMap.get(currentActiveId)?.pause();
      }

      if (knownDuration != null) setDuration(knownDuration);
      setActiveId(id);
      playStateRef.current = "loading";
      setPlayState("loading");
      setAnalyser(null);
      activeIdRef.current = id;

      startPlayback({ id, audioUrl, loop, failureMessage: "[AudioPlayer] Playback blocked or failed" });
    },
    [startPlayback],
  );

  const playFrom = useCallback(
    ({ id, audioUrl, loop, knownDuration }: AudioPlayerItem, startTime: number) => {
      const audioMap = audioMapRef.current;
      const currentActiveId = activeIdRef.current;
      const clampedStartTime = Math.max(0, knownDuration != null ? Math.min(startTime, knownDuration) : startTime);

      if (knownDuration != null) {
        knownDurationMapRef.current.set(id, knownDuration);
        setDuration(knownDuration);
      }

      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      if (currentActiveId === id) {
        seek(clampedStartTime);
        const audio = audioMap.get(id);
        if (audio && playStateRef.current !== "playing") {
          playStateRef.current = "loading";
          setPlayState("loading");
          audio
            .play({ time: clampedStartTime })
            .then(() => {
              if (activeIdRef.current === id) {
                playStateRef.current = "playing";
                setPlayState("playing");
                setAnalyser(analyserMapRef.current.get(id) ?? null);
              }
            })
            .catch((err: unknown) => {
              logger.warn("[AudioPlayer] Playback from waveform failed", { id, err });
              if (activeIdRef.current === id) {
                playStateRef.current = "idle";
                setPlayState("idle");
              }
            });
        }
        return;
      }

      if (currentActiveId) {
        audioMap.get(currentActiveId)?.pause();
      }

      setActiveId(id);
      setCurrentTime(clampedStartTime);
      playStateRef.current = "loading";
      setPlayState("loading");
      setAnalyser(null);
      activeIdRef.current = id;

      startPlayback({
        id,
        audioUrl,
        loop,
        startTime: clampedStartTime,
        failureMessage: "[AudioPlayer] Playback from waveform failed",
      });
    },
    [seek, startPlayback],
  );

  useEffect(() => {
    const audioMap = audioMapRef.current;
    return () => {
      if (closeTimerRef.current !== null) clearTimeout(closeTimerRef.current);
      audioMap.forEach(a => a.cleanup());
      audioMap.clear();
      analyserMapRef.current.clear();
    };
  }, []);

  return (
    <AudioPlayerContext.Provider
      value={{
        activeId,
        playState,
        analyser,
        currentTime,
        duration,
        handlePlay,
        playFrom,
        togglePlayPause,
        stopActive,
        seek,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
}

export { AudioPlayerContext };
export type { AudioPlayerContextValue };
